/**
 * Config save and load. Every change already writes to localStorage as it is
 * made (use-settings.ts), so there is no separate save action; this panel is
 * only the portable path in and out: a JSON file export and import, and a
 * reset back to the shipped defaults.
 */

import { useRef, type ReactElement } from 'react';

import type { SettingsController } from '../../hooks/use-settings.ts';

export function ConfigPanel({ settingsCtl }: { settingsCtl: SettingsController }): ReactElement {
  const fileInputRef = useRef<HTMLInputElement>(null);

  return (
    <section className="settings__section" aria-labelledby="settings-config-heading">
      <h2 id="settings-config-heading">Configuration</h2>
      <p className="muted">
        Every control on this tab saves to this browser as it changes. Export takes a snapshot to a
        file; import replaces the current settings with one.
      </p>
      <div className="settings__config-actions">
        <button type="button" className="button" onClick={settingsCtl.exportConfig}>
          Export config
        </button>
        <button
          type="button"
          className="button button--quiet"
          onClick={() => {
            fileInputRef.current?.click();
          }}
        >
          Import config
        </button>
        <input
          ref={fileInputRef}
          type="file"
          accept="application/json"
          className="settings__file-input"
          aria-label="Import config file"
          onChange={(event) => {
            const file = event.target.files?.[0];
            if (file !== undefined) void settingsCtl.importConfig(file);
            event.target.value = '';
          }}
        />
        <button type="button" className="button button--quiet" onClick={settingsCtl.reset}>
          Reset to defaults
        </button>
      </div>
    </section>
  );
}
